import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";
import { store } from "../../app/store/store";
import { setIsLoading } from "../common/commonSlice";

/**
 * V-Reference — the referral contact directory.
 *
 * The referral funnel. Kept here rather than in the view so the list, the detail modal and
 * the mobile twin all agree on the order and on the colours.
 */
export const VREFERENCE_STATUSES = ['new', 'contacted', 'interviewed', 'hired', 'not_retained']

/**
 * The pill colours, as VPlatform brand tokens rather than MUI palette names.
 *
 * MUI's `info` / `warning` / `success` / `error` resolve to its default blue/amber/green/red,
 * which is not this product's palette. The meanings follow `getTagClass()` in
 * app/utils/helpers.js so a status pill here reads the same as one anywhere else: grey for
 * not-started, blue for informational, orange for in progress, green for a positive outcome,
 * red for a negative one.
 *
 * Consumed as an `sx` fragment on a MUI Chip.
 */
export const VREFERENCE_STATUS_COLORS = {
    new: { backgroundColor: 'var(--lightGrey)', color: '#3a3a3a' },
    contacted: { backgroundColor: 'var(--blue)', color: '#fff' },
    interviewed: { backgroundColor: 'var(--orange)', color: '#fff' },
    hired: { backgroundColor: 'var(--green)', color: '#fff' },
    not_retained: { backgroundColor: 'var(--red)', color: '#fff' }
}

/** Fallback for a status the front end does not know about yet. */
export const VREFERENCE_STATUS_FALLBACK_COLOR = {
    backgroundColor: 'var(--lightGrey)', color: '#3a3a3a'
}

const initialState = {
    list: [],
    /** Poste-matched candidates for the Opportunity form. Summary rows only — no contact
     *  details; those arrive per-candidate via getVReferenceProfile. */
    suggestions: [],
    suggestionProfile: null,
    suggestionAttachments: [],
    postes: [],
    attachments: [],
    duplicates: [],
    query: "",
    statusFilter: "",
    /**
     * Mirrors the backend's own answer to "may this user manage the whole directory".
     *
     * Deliberately taken from the response rather than recomputed on the client: the server
     * already decided it when it filtered the rows, and having the UI derive the same
     * conclusion from the raw permissions array a second time is how the two drift apart.
     * It only ever hides buttons — the server enforces the rule regardless.
     */
    canManageAll: false,
    isLoading: false
}

export const VReferenceSlice = createSlice({
    name: "vreference",
    initialState,
    reducers: {
        setQuery: (state, action) => {
            state.query = action.payload
        },
        setStatusFilter: (state, action) => {
            state.statusFilter = action.payload
        },
        clearDuplicates: (state) => {
            state.duplicates = []
        },
        clearAttachments: (state) => {
            state.attachments = []
        },
        clearSuggestions: (state) => {
            state.suggestions = []
        },
        clearSuggestionProfile: (state) => {
            state.suggestionProfile = null
            state.suggestionAttachments = []
        }
    },
    extraReducers: (builder) => {
        const applyList = (state, action) => {
            const data = action.payload && action.payload.responseData
            if (data && data["list"]) {
                state.list = data["list"]
                state.canManageAll = !!data["canManageAll"]
            }
        }

        builder.addCase(getAllVReferences.fulfilled, applyList)
        builder.addCase(searchVReferences.fulfilled, applyList)

        builder.addCase(getVReferencePostes.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            if (data && data["list"]) state.postes = data["list"]
        })

        builder.addCase(checkVReferenceDuplicates.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            state.duplicates = (data && data["duplicates"]) || []
        })

        builder.addCase(getVReferenceSuggestions.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            state.suggestions = (data && data["list"]) || []
        })

        builder.addCase(getVReferenceProfile.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            state.suggestionProfile = (data && data["contact"]) || null
            state.suggestionAttachments = (data && data["attachments"]) || []
        })

        builder.addCase(listVReferenceAttachments.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            state.attachments = (data && data["list"]) || []
        })
    }
})

export const getAllVReferences = createAsyncThunk(
    "vreference/getAll",
    async (body) => {
        try {
            store.dispatch(setIsLoading(true))
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.getAll, body || {})
            store.dispatch(setIsLoading(false))
            return response
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return error
        }
    }
)

/**
 * Full-text search, including the text of every attached CV.
 *
 * No global loader on purpose: this fires as the user types, and flashing a full-screen
 * spinner on every keystroke is worse than a list that updates a moment later. The view
 * shows a local progress indicator instead.
 */
export const searchVReferences = createAsyncThunk(
    "vreference/search",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.search, body || {})
        } catch (error) {
            return error
        }
    }
)

export const checkVReferenceDuplicates = createAsyncThunk(
    "vreference/checkDuplicates",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.checkDuplicates, body || {})
        } catch (error) {
            return error
        }
    }
)

export const getVReferencePostes = createAsyncThunk(
    "vreference/getPostes",
    async () => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.getPostes, {})
        } catch (error) {
            return error
        }
    }
)

export const addVReference = createAsyncThunk(
    "vreference/add",
    async (body) => {
        try {
            store.dispatch(setIsLoading(true))
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.add, body)
            store.dispatch(setIsLoading(false))
            return response
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return error
        }
    }
)

export const updateVReference = createAsyncThunk(
    "vreference/update",
    async (body) => {
        try {
            store.dispatch(setIsLoading(true))
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.update, body)
            store.dispatch(setIsLoading(false))
            return response
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return error
        }
    }
)

export const updateVReferenceStatus = createAsyncThunk(
    "vreference/updateStatus",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.updateStatus, body)
        } catch (error) {
            return error
        }
    }
)

export const deleteVReference = createAsyncThunk(
    "vreference/delete",
    async (body) => {
        try {
            store.dispatch(setIsLoading(true))
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.delete, body)
            store.dispatch(setIsLoading(false))
            return response
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return error
        }
    }
)

export const listVReferenceAttachments = createAsyncThunk(
    "vreference/listAttachments",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.listAttachments, body)
        } catch (error) {
            return error
        }
    }
)

export const uploadVReferenceAttachment = createAsyncThunk(
    "vreference/uploadAttachment",
    async ({ file, idVReference }) => {
        try {
            store.dispatch(setIsLoading(true))
            let response = await NetworkManager.getInstance()
                .uploadVReferenceAttachment(file, idVReference)
            store.dispatch(setIsLoading(false))
            return { responseData: response }
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return { responseError: error && error.response }
        }
    }
)

export const deleteVReferenceAttachment = createAsyncThunk(
    "vreference/deleteAttachment",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.deleteAttachment, body)
        } catch (error) {
            return error
        }
    }
)

/**
 * Poste-matched candidates for an opportunity. Requires `opportunities:edit` server-side.
 *
 * No global loader: this fires as the form's poste/competences change and a full-screen
 * spinner on a side panel would be worse than the panel filling in a moment later.
 */
export const getVReferenceSuggestions = createAsyncThunk(
    "vreference/suggestions",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.suggestions, body)
        } catch (error) {
            return error
        }
    }
)

/** One candidate in full, for the profile modal opened from a suggestion. */
export const getVReferenceProfile = createAsyncThunk(
    "vreference/profile",
    async (body) => {
        try {
            store.dispatch(setIsLoading(true))
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.vreference.profile, body)
            store.dispatch(setIsLoading(false))
            return response
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return error
        }
    }
)

export const {
    setQuery, setStatusFilter, clearDuplicates, clearAttachments,
    clearSuggestions, clearSuggestionProfile
} = VReferenceSlice.actions

export default VReferenceSlice.reducer
